import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from '../../util/reactIntl';
import { optimizedNoticeUrl } from '../../util/euGuaranteeNotice';

import css from './ListingPage.module.css';

// Lightbox for the notice: dark backdrop, image as large as the screen allows (never cropped),
// a clearly visible close button, Esc / backdrop click to close, and a tap on the image to
// toggle between "fit to screen" and its full pixel size (scrollable) for the small print.
const NoticeLightbox = ({ src, onClose }) => {
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className={css.noticeLightbox} role="dialog" aria-modal="true" onClick={onClose}>
      <button type="button" className={css.noticeLightboxClose} onClick={onClose}>
        <span className={css.noticeLightboxCloseText}>
          <FormattedMessage id="Modal.close" />
        </span>
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M3 3l14 14M17 3L3 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
      <div className={css.noticeLightboxScroll}>
        <img
          className={zoomed ? css.noticeLightboxImageZoomed : css.noticeLightboxImage}
          src={src}
          alt=""
          onClick={e => {
            e.stopPropagation();
            setZoomed(z => !z);
          }}
        />
      </div>
    </div>,
    document.body
  );
};

/**
 * The seller's official "EU legal guarantee notice" (mandatory for sellers to consumers in the
 * EU from 27 Sept 2026), uploaded in their profile settings (Impressum section). Rendered
 * inside the "about the user" section as a small thumbnail; tapping it opens the notice in a
 * large lightbox, uncropped and in colour, readable on desktop, tablet and mobile. Renders
 * nothing when the seller hasn't uploaded one.
 */
const SectionEuGuaranteeNoticeMaybe = ({ listing }) => {
  const [isOpen, setIsOpen] = useState(false);
  const publicData = listing?.author?.attributes?.profile?.publicData || {};
  const { euGuaranteeNoticeUrl, euGuaranteeNoticeWidth, euGuaranteeNoticeHeight } = publicData;

  if (!euGuaranteeNoticeUrl || typeof euGuaranteeNoticeUrl !== 'string') {
    return null;
  }

  const src = optimizedNoticeUrl(euGuaranteeNoticeUrl);

  return (
    <div className={css.euGuaranteeNotice}>
      <h3 className={css.euGuaranteeNoticeTitle}>
        <FormattedMessage id="ListingPage.euGuaranteeNoticeTitle" />
      </h3>
      <button type="button" className={css.euGuaranteeNoticeThumbButton} onClick={() => setIsOpen(true)}>
        <img
          className={css.euGuaranteeNoticeThumb}
          src={src}
          alt=""
          width={euGuaranteeNoticeWidth || undefined}
          height={euGuaranteeNoticeHeight || undefined}
          loading="lazy"
        />
        <span className={css.euGuaranteeNoticeHint}>
          <FormattedMessage id="ListingPage.euGuaranteeNoticeEnlarge" />
        </span>
      </button>

      {isOpen ? <NoticeLightbox src={src} onClose={() => setIsOpen(false)} /> : null}
    </div>
  );
};

export default SectionEuGuaranteeNoticeMaybe;
